import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntities } from '../../manager/workflow-def.reducer';
import { formatWorkflowPeriodicidadLabel } from '../../shared/workflow-periodicidad';
import {
  PROCESOS_WORKFLOW_PAGE_SIZE,
  procesosWorkflowNombreFilters,
} from './procesos-workflow-routes';

export interface ProcesosWorkflowRow {
  id: number;
  nombre?: string;
  periodicidad?: string | null;
  workflowClase?: { nombre?: string } | null;
}

export interface ProcesosWorkflowsViewProps {
  active?: boolean;
  onSelectWorkflow: (workflow: ProcesosWorkflowRow) => void;
}

export function ProcesosWorkflowsView({
  active = true,
  onSelectWorkflow,
}: ProcesosWorkflowsViewProps) {
  const dispatch = useAppDispatch();
  const entities = useAppSelector(s => s.workflowDef.entities) as ProcesosWorkflowRow[];
  const totalItems = useAppSelector(s => s.workflowDef.totalItems);
  const loading = useAppSelector(s => s.workflowDef.loading);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PROCESOS_WORKFLOW_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'nombre', sort: 'asc' },
  ]);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!active) return;
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [active, debouncedQuery]);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [active]);

  const fetchWorkflows = useCallback(() => {
    const sortItem = sortModel[0] ?? { field: 'nombre', sort: 'asc' as const };
    dispatch(
      getEntities({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        sort: `${sortItem.field},${sortItem.sort}`,
        filters: procesosWorkflowNombreFilters(debouncedQuery),
      }),
    );
  }, [debouncedQuery, dispatch, paginationModel, sortModel]);

  useEffect(() => {
    if (!active) return;
    fetchWorkflows();
  }, [active, fetchWorkflows]);

  const cols: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 56 },
      {
        field: 'nombre',
        headerName: 'Nombre',
        flex: 1.4,
        minWidth: 160,
        renderCell: params => {
          const row = params.row as ProcesosWorkflowRow;
          const label = row.nombre?.trim() || `Workflow #${row.id}`;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              style={{ fontSize: 13, textDecoration: 'none' }}
              onClick={() => onSelectWorkflow(row)}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: 'periodicidad',
        headerName: 'Periodicidad',
        flex: 0.7,
        minWidth: 110,
        renderCell: params => formatWorkflowPeriodicidadLabel(params.value),
      },
      {
        field: 'workflowClase',
        headerName: 'Clase',
        flex: 0.8,
        minWidth: 110,
        sortable: false,
        renderCell: params => {
          const nombre = (params.row as ProcesosWorkflowRow).workflowClase?.nombre?.trim();
          return nombre || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
    ],
    [onSelectWorkflow],
  );

  return (
    <>
      <p style={s.hint}>Seleccione un workflow para ver sus periodos generados y crear procesos.</p>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Buscar por nombre…"
        style={s.input}
        aria-label="Buscar workflow por nombre"
        autoComplete="off"
      />
      <DataGrid
        rows={entities ?? []}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        pagination
        paginationMode="server"
        sortingMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        paginationModel={paginationModel}
        onPaginationModelChange={model => {
          setPaginationModel({ ...model, pageSize: PROCESOS_WORKFLOW_PAGE_SIZE });
        }}
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(model.length > 0 ? model : [{ field: 'nombre', sort: 'asc' }]);
        }}
        onRowClick={params => onSelectWorkflow(params.row as ProcesosWorkflowRow)}
        rowCount={totalItems ?? (entities ?? []).length}
        loading={loading}
        pageSizeOptions={[PROCESOS_WORKFLOW_PAGE_SIZE]}
        localeText={{ noRowsLabel: query.trim() ? 'Sin workflows para esa búsqueda' : 'Sin workflows' }}
        sx={{
          ...profaseDataGridSx,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />
    </>
  );
}

interface ProcesosWorkflowsModalProps {
  open: boolean;
  onClose: () => void;
  onSelectWorkflow: (workflow: ProcesosWorkflowRow) => void;
}

export default function ProcesosWorkflowsModal({
  open,
  onClose,
  onSelectWorkflow,
}: ProcesosWorkflowsModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-workflows-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-workflows-modal-title" className="profase-modal-title m-0">
          <FaProjectDiagram style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Workflows
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <ProcesosWorkflowsView active={open} onSelectWorkflow={onSelectWorkflow} />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
    background: 'var(--crm-input-bg, #fff)',
    color: 'var(--crm-text-primary, #0f172a)',
    boxSizing: 'border-box',
    marginBottom: 12,
  },
};
